<script lang="ts">
  import { STORE, MAX_RETAINED_ITEMS, type PoolStat } from "../../state.svelte";
  import { shortAddress } from "../../utils";
  import PoolBadge from "../elements/PoolBadge.svelte";
  import { onMount } from "svelte";

  let { onSelectPool = (_addr: string) => {} }: { onSelectPool?: (addr: string) => void } = $props();

  // All computed via interval, not $derived
  let sortedPools: PoolStat[] = $state([]);
  let maxTrades = $state(1);
  let totalPools = $state(0);
  let totalTrades = $state(0);
  let totalWallets = $state(0);
  let dexShare: [string, number][] = $state([]);
  let dexTotal = $state(0);
  let highConcentrationPools = $state(0);
  let avgTradesPerWallet = $state("0");

  onMount(() => {
    const iv = setInterval(() => {
      const pools = Object.values(STORE.bot.poolsStats);
      sortedPools = [...pools]
        .sort((a, b) => b.tradesRawCount - a.tradesRawCount)
        .slice(0, MAX_RETAINED_ITEMS);
      maxTrades = sortedPools.length > 0 ? sortedPools[0].tradesRawCount : 1;
      totalPools = pools.length;

      let tt = 0;
      let hc = 0;
      let ratioSum = 0;
      const walletSet = new Set<string>();
      const dist: Record<string, number> = {};

      for (const p of pools) {
        tt += p.tradesRawCount;
        dist[p.pool_type] = (dist[p.pool_type] || 0) + p.tradesRawCount;
        const wc = Object.keys(p.wallets).length;
        for (const w of Object.keys(p.wallets)) walletSet.add(w);
        if (wc > 0) {
          const ratio = p.tradesRawCount / wc;
          ratioSum += ratio;
          if (ratio > 5) hc++;
        }
      }

      totalTrades = tt;
      totalWallets = walletSet.size;
      highConcentrationPools = hc;
      avgTradesPerWallet = pools.length > 0 ? (ratioSum / pools.length).toFixed(1) : "0";
      dexShare = Object.entries(dist).sort((a, b) => b[1] - a[1]);
      dexTotal = tt;

      // Retry pool info for top pools missing it
      for (const p of sortedPools.slice(0, 20)) {
        if (STORE.bot.sailfish.getPoolInfo(p.pool_address) === null) {
          STORE.bot.sailfish.fetchPoolInfo(p.pool_address).catch(() => {});
        }
      }
    }, 2000);
    return () => clearInterval(iv);
  });

  const dexColors: Record<string, string> = {
    PumpSwapAmm: "bg-[#ff6b9d]",
    PumpFunAmm: "bg-[#00d18c]",
    RaydiumAmm: "bg-[#6c5ce7]",
    RaydiumCpmm: "bg-[#8b7cf7]",
    RaydiumClmm: "bg-[#a39af7]",
    RaydiumLaunchpad: "bg-[#5b4cd6]",
    MeteoraDyn: "bg-[#f5a623]",
    MeteoraDynV2: "bg-[#f7b84e]",
    MeteoraDlmm: "bg-[#e8961c]",
  };
  const getDexColor = (dex: string) => dexColors[dex] || "bg-gray-500";

  // Cache last known price per pool so it persists after trades rotate out
  const priceCache = new Map<string, number>();

  const getPoolPrice = (poolAddress: string) => {
    const trade = STORE.bot.trades.find((t) => t.pool_address === poolAddress);
    if (trade) {
      const p = parseFloat(trade.price) || 0;
      if (p > 0) priceCache.set(poolAddress, p);
      return p || priceCache.get(poolAddress) || null;
    }
    return priceCache.get(poolAddress) || null;
  };

  const formatPrice = (p: number) => {
    if (p >= 1) return p.toFixed(4);
    if (p >= 0.001) return p.toFixed(6);
    return p.toFixed(9);
  };

  const getPairName = (pool: PoolStat) => {
    const info = STORE.bot.sailfish.getPoolInfo(pool.pool_address);
    if (info) {
      return `${info.base_token.symbol} / ${info.quote_token.symbol}`;
    }
    return `${shortAddress(pool.token_0_mint, 4, 4)} / ${shortAddress(pool.token_1_mint, 4, 4)}`;
  };
</script>

<div class="px-4">
  <!-- Widgets Row -->
  <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 mb-6">
    <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-4 text-center">
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">Pools Tracked</p>
      <p class="text-2xl font-black text-white tabular-nums">{totalPools}</p>
    </div>
    <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-4 text-center">
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">Total Trades</p>
      <p class="text-2xl font-black text-white tabular-nums">{totalTrades.toLocaleString()}</p>
    </div>
    <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-4 text-center">
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">Unique Wallets</p>
      <p class="text-2xl font-black text-white tabular-nums">{totalWallets}</p>
    </div>
    <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-4 text-center">
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">Avg Trades/Wallet</p>
      <p class="text-2xl font-black text-white tabular-nums">{avgTradesPerWallet}</p>
    </div>
    <div class="bg-gray-950/40 border border-sky-900/30 rounded-sm p-4 text-center">
      <p class="text-[10px] font-mono text-sky-500/60 uppercase tracking-widest mb-1">Bot-like Pools</p>
      <p class="text-2xl font-black text-sky-500 tabular-nums">{highConcentrationPools}</p>
      <p class="text-[9px] text-gray-600 font-mono mt-1">&gt;5 trades/wallet</p>
    </div>
  </div>

  <!-- DEX Market Share -->
  {#if dexShare.length > 0}
    <div class="mb-6">
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-2">DEX Market Share</p>
      <div class="h-2 bg-gray-800 rounded-full flex overflow-hidden mb-2">
        {#each dexShare as [dex, count]}
          <div class="{getDexColor(dex)} transition-all duration-500" style="width: {(count / dexTotal) * 100}%"></div>
        {/each}
      </div>
      <div class="flex flex-wrap gap-x-4 gap-y-1 text-[10px] font-mono text-gray-500">
        {#each dexShare as [dex, count]}
          <span class="flex items-center gap-1">
            <span class="w-2 h-2 {getDexColor(dex)} rounded-full inline-block"></span>
            {dex} <span class="text-gray-700">{Math.round((count / dexTotal) * 100)}%</span>
          </span>
        {/each}
      </div>
    </div>
  {/if}

  <!-- Pool Table -->
  <div class="overflow-x-auto">
    <table class="w-full text-xs font-mono">
      <thead>
        <tr class="text-gray-600 text-left border-b border-gray-800">
          <th class="py-1.5 px-2 font-normal w-8 hidden sm:table-cell">#</th>
          <th class="py-1.5 px-2 font-normal">DEX</th>
          <th class="py-1.5 px-2 font-normal">Pair</th>
          <th class="py-1.5 px-2 font-normal text-right">Trades</th>
          <th class="py-1.5 px-2 font-normal text-right hidden sm:table-cell">Wallets</th>
          <th class="py-1.5 px-2 font-normal text-right">Price</th>
          <th class="py-1.5 px-2 font-normal text-right hidden md:table-cell">Address</th>
        </tr>
      </thead>
      <tbody>
        {#each sortedPools as pool, index}
          <tr class="border-b border-gray-800/50 hover:bg-gray-900/50">
            <td class="py-1.5 px-2 text-gray-700 tabular-nums hidden sm:table-cell">{String(index + 1).padStart(2, "0")}</td>
            <td class="py-1.5 px-2"><PoolBadge poolType={pool.pool_type} /></td>
            <td class="py-1.5 px-2">
              <button class="text-white hover:text-sky-500 transition-colors text-left cursor-pointer" onclick={() => onSelectPool(pool.pool_address)}>
                {getPairName(pool)}
              </button>
              <div class="h-0.5 bg-gray-800 rounded-full mt-1 w-full max-w-24">
                <div class="h-full bg-sky-600 rounded-full" style="width: {(pool.tradesRawCount / maxTrades) * 100}%"></div>
              </div>
            </td>
            <td class="py-1.5 px-2 text-right text-white tabular-nums">{pool.tradesRawCount}</td>
            <td class="py-1.5 px-2 text-right text-gray-400 tabular-nums hidden sm:table-cell">{Object.keys(pool.wallets).length}</td>
            <td class="py-1.5 px-2 text-right text-white tabular-nums">
              {#if getPoolPrice(pool.pool_address)}
                {formatPrice(getPoolPrice(pool.pool_address)!)}
              {:else}
                <span class="text-gray-700">—</span>
              {/if}
            </td>
            <td class="py-1.5 px-2 text-right hidden md:table-cell">
              <a href={`https://dexscreener.com/solana/${pool.pool_address}`} target="_blank" class="text-gray-600 hover:text-white transition-colors">{shortAddress(pool.pool_address, 4, 4)}</a>
            </td>
          </tr>
        {/each}
        {#if sortedPools.length === 0}
          <tr><td colspan="7" class="py-8 text-center text-gray-600 italic">Waiting for pool activity...</td></tr>
        {/if}
      </tbody>
    </table>
  </div>
</div>
